import { useEffect, useState, useCallback, useRef } from 'react';
import { isStandalone, isIOSSafari, isMacSafari } from '../../shared/pwaUtility';

const DISMISS_KEY = 'blitzz_pwa_install_dismissed';
const DISMISS_DURATION_DAYS = 7;

const isDismissed = () => {
  try {
    const data = localStorage.getItem(DISMISS_KEY);
    if (!data) return false;
    const { ts } = JSON.parse(data);
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DURATION_DAYS;
  } catch (e) {
    return false;
  }
};

export const usePWAInstall = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [platform, setPlatform] = useState('none');
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setPlatform('chromium');
      setCanInstall(true);
    };

    const handleInstalled = () => {
      deferredPromptRef.current = null;
      setCanInstall(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    // iOS / Mac Safari don't fire `beforeinstallprompt` — surface the UI on mount.
    if (isIOSSafari()) {
      setPlatform('ios');
      setCanInstall(true);
    } else if (isMacSafari()) {
      setPlatform('mac-safari');
      setCanInstall(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = deferredPromptRef.current;
    if (!evt) return false;
    evt.prompt();
    const { outcome } = await evt.userChoice;
    deferredPromptRef.current = null;
    setCanInstall(false);
    return outcome === 'accepted';
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify({ ts: Date.now() }));
    } catch (e) {
      // Storage may be blocked; UI still hides for the session
    }
    setCanInstall(false);
  }, []);

  return { canInstall, platform, promptInstall, dismiss };
};
